const FREQUENCY_MULTIPLIERS = {
  monthly: 12,
  semimonthly: 24,
  quarterly: 4,
  annual: 1,
};

export const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const roundMoney = (value) => (
  Math.round((toNumber(value) + Number.EPSILON) * 100) / 100
);

export const annualize = (amount, frequency = 'annual', customMultiplier = null) => {
  const multiplier = frequency === 'custom'
    ? toNumber(customMultiplier)
    : (FREQUENCY_MULTIPLIERS[frequency] || 1);
  return roundMoney(toNumber(amount) * multiplier);
};

export const calculateVariance = (previous, forecast) => (
  toNumber(previous) === 0
    ? null
    : (toNumber(forecast) - toNumber(previous)) / Math.abs(toNumber(previous))
);

export const deriveRevenueLine = (line) => ({
  ...line,
  variance: calculateVariance(line.previous_amount, line.forecast_amount),
});

export const deriveLabourLine = (line) => {
  const isHourly = String(line.labour_group || '').endsWith('hourly');
  const base = isHourly
    ? (toNumber(line.hourly_rate) * toNumber(line.annual_hours)) + toNumber(line.bonus_benefit)
    : toNumber(line.annual_salary) + toNumber(line.bonus_benefit);
  return {
    ...line,
    budgeted_amount: roundMoney(
      base * toNumber(line.allocation_pct) * toNumber(line.headcount || 1)
    ),
  };
};

export const deriveEquipmentLine = (line) => ({
  ...line,
  annual_amount: roundMoney(
    annualize(line.periodic_amount, line.frequency, line.custom_multiplier)
      * toNumber(line.quantity || 1)
  ),
});

export const deriveExpenseLine = (line) => {
  const hasOverride = line.annual_forecast_override !== ''
    && line.annual_forecast_override !== null
    && line.annual_forecast_override !== undefined;
  const annualForecast = hasOverride
    ? roundMoney(line.annual_forecast_override)
    : annualize(line.periodic_amount, line.frequency, line.custom_multiplier);
  return {
    ...line,
    annual_forecast: annualForecast,
    allocated_forecast: roundMoney(annualForecast * toNumber(line.allocation_pct)),
    variance: calculateVariance(line.previous_amount, annualForecast),
  };
};

export const sumLines = (lines, selector) => roundMoney(
  lines.reduce((total, line) => total + toNumber(selector(line)), 0)
);

export const calculateLabourSectionTotal = (lines, settings) => {
  const fieldPayroll = sumLines(
    lines.filter((line) => String(line.labour_group).startsWith('field_')),
    (line) => line.budgeted_amount
  );
  const overheadPayroll = sumLines(
    lines.filter((line) => String(line.labour_group).startsWith('overhead_')),
    (line) => line.budgeted_amount
  );
  return roundMoney(
    fieldPayroll
      + (fieldPayroll * toNumber(settings?.field_burden_pct))
      + overheadPayroll
      + (overheadPayroll * toNumber(settings?.overhead_burden_pct))
  );
};

export const clearStaleExpenseOverride = (line, changedKey) => (
  ['periodic_amount', 'frequency', 'custom_multiplier'].includes(changedKey)
    ? { ...line, annual_forecast_override: '' }
    : line
);
